import React, { 
  createContext, 
  useContext, 
  useEffect, 
  useState, 
  useCallback, 
  useRef,
  useMemo 
} from 'react';
import { io } from 'socket.io-client';
import { getAuthToken } from '../API/token-util';
import { getAuthToken as getCreatorToken, getAuthInfo as getCreatorInfo } from '../API/creatorTokenUtil';
import { useQueryClient } from 'react-query';


// Extracting base URL without /api suffix for socket connection
const API_BASE = process.env.REACT_APP_BASE_URL || 'https://blitz-app-u3rzf.ondigitalocean.app/api';
const SOCKET_URL = API_BASE.replace(/\/api\/?$/, ''); // Remove /api suffix

const SOCKET_CONFIG = {
  namespace: '/chat',
  path: '/socket.io',
  reconnectionAttempts: 10,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 10000,
  timeout: 20000,
  transports: ['websocket', 'polling'], // Try WebSocket first, fallback to polling
};

// Connection state enum
const CONNECTION_STATES = {
  DISCONNECTED: 'disconnected',
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  RECONNECTING: 'reconnecting',
  ERROR: 'error',
};

// Valid reaction types (must match backend)
const VALID_REACTIONS = ['like', 'love', 'laugh', 'wow', 'sad', 'angry'];

const SocketContext = createContext(null);

/**
 * Hook to access socket context
 * @returns {Object} Socket context value
 */

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    return {
      socket: null,
      isConnected: false,
      connectionState: CONNECTION_STATES.DISCONNECTED,
      globalUserId: null,
      lastError: null,
      joinConversation: () => {},
      leaveConversation: () => {},
      sendMessage: () => Promise.reject(new Error('Socket not available')),
      sendTypingStatus: () => {},
      markAsRead: () => {},
      addReaction: () => {},
      removeReaction: () => {},
      pinMessage: () => {},
      unpinMessage: () => {},
      deleteMessage: () => {},
      editMessage: () => {},
      fetchMessages: () => {},
      updateStatus: () => {},
      acceptRequest: () => {},
      getStatus: () => {},
      addParticipant: () => {},
      removeParticipant: () => {},
      transferHost: () => {},
      updateConversation: () => {},
      getOnlineUsers: () => {},
      onlineUsers: [],
      typingUsers: {},
      pinnedMessages: {},
      getTypingUsers: () => [],
      isUserOnline: () => false,
      roomPresence: {},
      getRoomOnlineUsers: () => ({ online_users: [], online_count: 0 }),
      reconnect: () => {},
      CONNECTION_STATES,
      VALID_REACTIONS,
    };
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  // State
  const [socket, setSocket] = useState(null);
  const [connectionState, setConnectionState] = useState(CONNECTION_STATES.DISCONNECTED);
  const [globalUserId, setGlobalUserId] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [typingUsers, setTypingUsers] = useState({});
  const [joinedRooms, setJoinedRooms] = useState(new Set());
  const [pinnedMessages, setPinnedMessages] = useState({});
  const [roomPresence, setRoomPresence] = useState({}); // { [conversationId]: { online_users: [], online_count: 0 } }
  const [lastError, setLastError] = useState(null);
  
  // Refs
  const socketRef = useRef(null);
  const reconnectAttemptRef = useRef(0);
  const typingTimeoutsRef = useRef({});
  const joinedRoomsRef = useRef(new Set()); // Separate ref for reconnection logic
  
  // Handler refs - always point to latest handlers without causing reconnection
  const handlersRef = useRef({});
  
  const queryClient = useQueryClient();
//Auth Token Retrieval  
  const getActiveToken = useCallback(() => {
    // Try business user token first, then creator token
    const businessToken = getAuthToken();
    const creatorToken = getCreatorToken();
    return businessToken || creatorToken || null;
  }, []);

  const getUserIdentifier = useCallback(() => {
    const creatorInfo = getCreatorInfo();
    if (creatorInfo?.creator_user?.username) {
      return { type: 'creator', id: creatorInfo.creator_user.username };
    }
    // For business users, token decode happens on server
    return { type: 'user', id: null };
  }, []);

    //Message Handlers (defined before socket initialization)

   //Handle incoming new message - update React Query cache
 
  /**
   * Handle incoming new message - update React Query cache
   * Now supports server-side optimistic updates with is_pending flag
   */
  const handleNewMessage = useCallback((data) => {
    const { conversation_id, ...messageData } = data;
    
    if (!conversation_id) {
      console.warn('[Socket] Received message without conversation_id:', data);
      return;
    }

    // List of potential cache keys that might store messages for this conversation
    // Include both number and string variants to handle type mismatches
    const messageCacheKeys = [
      ['messages', conversation_id],
      ['messages', String(conversation_id)],
      ['campaign_messages', conversation_id],
      ['campaign_messages', String(conversation_id)],
      ['bubble_messages', conversation_id],
      ['bubble_messages', String(conversation_id)],
      ['bubble_messages_preview', conversation_id],
      ['bubble_messages_preview', String(conversation_id)]
    ];

    // Update all relevant message caches
    messageCacheKeys.forEach(queryKey => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;

        const existingMessages = oldData.messages || [];
        
        // Find existing message by client_temp_id (for sender's optimistic update)
        // or by id if available - use String() comparison to handle type mismatches
        const existingMessageIndex = existingMessages.findIndex(
          (m) => {
            // Match by client_temp_id first (most reliable for sender)
            if (messageData.client_temp_id && m.client_temp_id === messageData.client_temp_id) {
              return true;
            }
            // Match by temp id used as message id (client-side optimistic)
            if (messageData.client_temp_id && String(m.id) === String(messageData.client_temp_id)) {
              return true;
            }
            // Match by real id (for non-sender updates)
            if (messageData.id && String(m.id) === String(messageData.id)) {
              return true;
            }
            return false;
          }
        );

        if (existingMessageIndex !== -1) {
          // Update existing optimistic message with server response
          const newMessages = [...existingMessages];
          newMessages[existingMessageIndex] = { 
            ...newMessages[existingMessageIndex],
            ...messageData,
            // Keep is_pending from server, remove client-side flags
            isOptimistic: false,
            sending: false
          };
          return {
            ...oldData,
            messages: newMessages,
          };
        }

        // Add new message to the end of the list (for receiver)
        return {
          ...oldData,
          messages: [...existingMessages, messageData],
        };
      });
    });

    // Invalidate conversation list queries to update preview/unread counts
    queryClient.invalidateQueries(['conversations', 'organized']);
    queryClient.invalidateQueries(['creatorConversationsOrganized']);
    queryClient.invalidateQueries(['messages', 'userOrganizedConversations']);
    queryClient.invalidateQueries({ queryKey: ['creatorConversations'], exact: false });
    queryClient.invalidateQueries(['user', 'dashboard']);
  }, [queryClient]);

  /**
   * Handle message confirmation - update message with real ID after DB save
   */
  const handleMessageConfirmed = useCallback((data) => {
    const { conversation_id, client_temp_id, message_id } = data;
    
    if (!conversation_id || !client_temp_id) {
      console.warn('[Socket] Invalid message_confirmed data:', data);
      return;
    }

    // Include both number and string variants to handle type mismatches
    const messageCacheKeys = [
      ['messages', conversation_id],
      ['messages', String(conversation_id)],
      ['campaign_messages', conversation_id],
      ['campaign_messages', String(conversation_id)],
      ['bubble_messages', conversation_id],
      ['bubble_messages', String(conversation_id)],
      ['bubble_messages_preview', conversation_id],
      ['bubble_messages_preview', String(conversation_id)]
    ];

    messageCacheKeys.forEach(queryKey => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.messages) return oldData;

        return {
          ...oldData,
          messages: oldData.messages.map((msg) => {
            // Find message by client_temp_id and update with real ID
            if (msg.client_temp_id === client_temp_id || String(msg.id) === String(client_temp_id)) {
              return {
                ...msg,
                id: message_id,
                is_pending: false,
                isOptimistic: false,
                sending: false
              };
            }
            return msg;
          }),
        };
      });
    });
  }, [queryClient]);

  /**
   * Handle message failed - remove or mark message as failed
   */
  const handleMessageFailed = useCallback((data) => {
    const { conversation_id, client_temp_id, error } = data;
    
    console.error('[Socket] Message failed:', error);
    
    if (!conversation_id || !client_temp_id) return;

    // Include both number and string variants to handle type mismatches
    const messageCacheKeys = [
      ['messages', conversation_id],
      ['messages', String(conversation_id)],
      ['campaign_messages', conversation_id],
      ['campaign_messages', String(conversation_id)],
      ['bubble_messages', conversation_id],
      ['bubble_messages', String(conversation_id)],
      ['bubble_messages_preview', conversation_id],
      ['bubble_messages_preview', String(conversation_id)]
    ];

    messageCacheKeys.forEach(queryKey => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.messages) return oldData;

        return {
          ...oldData,
          messages: oldData.messages.map((msg) => {
            if (msg.client_temp_id === client_temp_id || String(msg.id) === String(client_temp_id)) {
              return {
                ...msg,
                is_failed: true,
                is_pending: false,
                error_message: error || 'Failed to send message'
              };
            }
            return msg;
          }),
        };
      });
    });
  }, [queryClient]);

  
   // Handle typing indicator updates

  const handleTypingStatus = useCallback((data) => {
    const { conversation_id, user_id, is_typing, display_name } = data;
    
    if (!conversation_id || !user_id) return;

    setTypingUsers((prev) => {
      const currentTyping = prev[conversation_id] || [];
      
      if (is_typing) {
        // Add user to typing list if not already present
        if (!currentTyping.find((u) => u.user_id === user_id)) {
          return {
            ...prev,
            [conversation_id]: [...currentTyping, { user_id, display_name }],
          };
        }
      } else {
        // Remove user from typing list
        return {
          ...prev,
          [conversation_id]: currentTyping.filter((u) => u.user_id !== user_id),
        };
      }
      return prev;
    });

    // Auto-clear typing indicator after 5 seconds (fallback)
    const timeoutKey = `${conversation_id}_${user_id}`;
    if (typingTimeoutsRef.current[timeoutKey]) {
      clearTimeout(typingTimeoutsRef.current[timeoutKey]);
    }
    
    if (is_typing) {
      typingTimeoutsRef.current[timeoutKey] = setTimeout(() => {
        setTypingUsers((prev) => ({
          ...prev,
          [conversation_id]: (prev[conversation_id] || []).filter((u) => u.user_id !== user_id),
        }));
      }, 5000);
    }
  }, []);

  /**
   * Handle bulk typing users update (new server event with richer data)
   */
  const handleTypingUsers = useCallback((data) => {
    const { conversation_id, typing_users: users } = data;
    if (!conversation_id) return;
    setTypingUsers((prev) => ({
      ...prev,
      [conversation_id]: (users || []).map((u) => ({
        user_id: u.user_id,
        display_name: u.display_name || 'Someone',
        avatar: u.avatar || null,
      })),
    }));
  }, []);

  /**
   * Handle per-conversation room presence updates
   */
  const handleRoomPresence = useCallback((data) => {
    const { conversation_id, online_users, online_count } = data;
    if (!conversation_id) return;
    setRoomPresence((prev) => ({
      ...prev,
      [conversation_id]: { online_users: online_users || [], online_count: online_count || 0 },
    }));
  }, []);

  /**
   * Handle read receipt updates
   */
  const handleMessagesRead = useCallback((data) => {
    const { conversation_id, reader_id, last_message_id, read_at } = data;
    
    if (!conversation_id) return;

    const idVariants = [conversation_id, String(conversation_id)];
    const baseKeys = ['messages', 'campaign_messages', 'bubble_messages', 'bubble_messages_preview'];
    const messageCacheKeys = baseKeys.flatMap((base) =>
      [...new Set(idVariants)].map((id) => [base, id])
    );

    messageCacheKeys.forEach(queryKey => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.messages) return oldData;

        return {
          ...oldData,
          messages: oldData.messages.map((msg) => {
            if (!last_message_id || msg.id <= last_message_id) {
              const existingReceipts = msg.read_receipts || {};
              return {
                ...msg,
                read_by: [...(msg.read_by || []), reader_id].filter(
                  (v, i, a) => a.indexOf(v) === i
                ),
                read_receipts: {
                  ...existingReceipts,
                  [String(reader_id)]: read_at || new Date().toISOString(),
                },
              };
            }
            return msg;
          }),
        };
      });
    });

    // Invalidate conversation list queries so unread badges update
    queryClient.invalidateQueries(['conversations', 'organized']);
    queryClient.invalidateQueries(['creatorConversationsOrganized']);
    queryClient.invalidateQueries(['messages', 'userOrganizedConversations']);
    queryClient.invalidateQueries({ queryKey: ['creatorConversations'], exact: false });
    queryClient.invalidateQueries(['user', 'dashboard']);
  }, [queryClient]);

  /**
   * Handle reaction updates (supports Slack-style multiple reactions per user)
   */
  const handleReactionUpdate = useCallback((data) => {
    const { conversation_id, message_id, reaction_counts, total_reactions, user_id, user_reactions } = data;
    
    if (!conversation_id || !message_id) return;

    const idVariants = [conversation_id, String(conversation_id)];
    const baseKeys = ['messages', 'campaign_messages', 'bubble_messages', 'bubble_messages_preview'];
    const messageCacheKeys = baseKeys.flatMap((base) =>
      [...new Set(idVariants)].map((id) => [base, id])
    );

    messageCacheKeys.forEach(queryKey => {
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData?.messages) return oldData;

        const updatedMessages = oldData.messages.map((msg) => {
          if (String(msg.id) === String(message_id)) {
            const updated = {
              ...msg,
              reaction_counts: reaction_counts || msg.reaction_counts,
              total_reactions: total_reactions ?? msg.total_reactions,
            };
            // Only update user_reactions if this event was from the current user
            if (String(user_id) === String(globalUserId)) {
              updated.user_reactions = user_reactions || [];
            }
            return updated;
          }
          return msg;
        });

        return {
          ...oldData,
          messages: updatedMessages,
        };
      });
    });
  }, [queryClient, globalUserId]);

  /**
   * Handle presence updates (online/offline)
   */
  const handlePresenceUpdate = useCallback((data) => {
    const { user_id, status } = data;
    
    if (!user_id) return;

    setOnlineUsers((prev) => {
      const newSet = new Set(prev);
      if (status === 'online') {
        newSet.add(user_id);
      } else {
        newSet.delete(user_id);
      }
      return newSet;
    });
  }, []);

  /**
   * Handle being added to a new conversation
   */
  const handleAddedToConversation = useCallback((data) => {
    const { conversation_id, conversation } = data;
    
    // Invalidate conversation list to show new conversation
    queryClient.invalidateQueries(['conversations', 'organized']);
    
    // Auto-join the new conversation room
    if (socketRef.current && conversation_id) {
      socketRef.current.emit('join_convo', { conversation_id });
      setJoinedRooms((prev) => new Set([...prev, conversation_id]));
    }
  }, [queryClient]);

  /**
   * Handle being removed from a conversation
   */
  const handleRemovedFromConversation = useCallback((data) => {
    const { conversation_id } = data;
    
    // Remove from local cache
    queryClient.removeQueries(['messages', conversation_id]);
    queryClient.invalidateQueries(['conversations', 'organized']);
    
    // Leave the room
    setJoinedRooms((prev) => {
      const newSet = new Set(prev);
      newSet.delete(conversation_id);
      return newSet;
    });
  }, [queryClient]);

  /**
   * Handle message deletion
   */
  const handleMessageDeleted = useCallback((data) => {
    const { conversation_id, message_id } = data;
    
    if (!conversation_id || !message_id) return;

    queryClient.setQueryData(['messages', conversation_id], (oldData) => {
      if (!oldData?.messages) return oldData;

      return {
        ...oldData,
        messages: oldData.messages.filter((msg) => msg.id !== message_id),
      };
    });
  }, [queryClient]);

  /**
   * Handle message edit
   */
  const handleMessageEdited = useCallback((data) => {
    const { conversation_id, message_id, message } = data;
    
    if (!conversation_id || !message_id) return;

    queryClient.setQueryData(['messages', conversation_id], (oldData) => {
      if (!oldData?.messages) return oldData;

      return {
        ...oldData,
        messages: oldData.messages.map((msg) =>
          msg.id === message_id ? { ...msg, ...message, edited: true } : msg
        ),
      };
    });
  }, [queryClient]);

  // Handle message pinned
  const handleMessagePinned = useCallback((data) => {
    const { conversation_id, pinned_message_ids } = data;
    if (!conversation_id) return;
    setPinnedMessages((prev) => ({ ...prev, [conversation_id]: pinned_message_ids || [] }));
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  // Handle message unpinned
  const handleMessageUnpinned = useCallback((data) => {
    const { conversation_id, pinned_message_ids } = data;
    if (!conversation_id) return;
    setPinnedMessages((prev) => ({ ...prev, [conversation_id]: pinned_message_ids || [] }));
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  // Handle host transferred
  const handleHostTransferred = useCallback((data) => {
    const { conversation_id } = data;
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  // Handle conversation updated
  const handleConversationUpdated = useCallback((data) => {
    const { conversation_id } = data;
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
    queryClient.invalidateQueries(['conversations', 'organized']);
  }, [queryClient]);

  // Handle status updated
  const handleStatusUpdated = useCallback((data) => {
    queryClient.invalidateQueries(['conversations', 'organized']);
  }, [queryClient]);

  // Handle request accepted
  const handleRequestAccepted = useCallback((data) => {
    const { conversation_id } = data;
    queryClient.invalidateQueries(['conversations', 'organized']);
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  // Handle messages fetched (pagination response)
  const handleMessagesFetched = useCallback((data) => {
    const { conversation_id, messages, before_id } = data;
    if (before_id && messages?.length) {
      queryClient.setQueryData(['messages', conversation_id], (oldData) => {
        if (!oldData) return { messages, conversation: null };
        const existingIds = new Set(oldData.messages.map((m) => m.id));
        const newMsgs = messages.filter((m) => !existingIds.has(m.id));
        return { ...oldData, messages: [...newMsgs, ...oldData.messages] };
      });
    }
  }, [queryClient]);

  // Handle online users status response
  const handleOnlineUsersStatus = useCallback((data) => {
    const users = data.users || data;
    Object.entries(users).forEach(([uid, info]) => {
      setOnlineUsers((prev) => {
        const newSet = new Set(prev);
        if (info.status === 'online') newSet.add(uid);
        else newSet.delete(uid);
        return newSet;
      });
    });
  }, []);

  // Handle participant changes
  const handleParticipantAdded = useCallback((data) => {
    const { conversation_id } = data;
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  const handleParticipantRemoved = useCallback((data) => {
    const { conversation_id } = data;
    queryClient.invalidateQueries(['messages', 'metadata', conversation_id]);
  }, [queryClient]);

  // Handle errors
  const handleError = useCallback((data) => {
    console.error('[Socket] Error:', data);
    setLastError(data);
  }, []);

  /**
   * Handle incoming notification (e.g. mention notifications) – refresh badge counts
   */
  const handleNotificationCreated = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['notifications'], exact: false });
  }, [queryClient]);

  // Keep handlers ref updated (so socket events always call latest handlers)
  useEffect(() => {
    handlersRef.current = {
      handleNewMessage,
      handleMessageConfirmed,
      handleMessageFailed,
      handleTypingStatus,
      handleMessagesRead,
      handleReactionUpdate,
      handlePresenceUpdate,
      handleAddedToConversation,
      handleRemovedFromConversation,
      handleMessageDeleted,
      handleMessageEdited,
      handleMessagePinned,
      handleMessageUnpinned,
      handleMessagesFetched,
      handleOnlineUsersStatus,
      handleParticipantAdded,
      handleParticipantRemoved,
      handleHostTransferred,
      handleConversationUpdated,
      handleStatusUpdated,
      handleRequestAccepted,
      handleTypingUsers,
      handleRoomPresence,
      handleNotificationCreated,
      handleError,
    };
  });

  // Keep joinedRoomsRef in sync
  useEffect(() => {
    joinedRoomsRef.current = joinedRooms;
  }, [joinedRooms]);


  // SOCKET INITIALIZATION & LIFECYCLE
  

  useEffect(() => {
    const token = getActiveToken();
    
    // Don't connect if no token
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setConnectionState(CONNECTION_STATES.DISCONNECTED);
      }
      return;
    }

    // Don't reinitialize if already connected with same token
    if (socketRef.current?.connected) {
      return;
    }

    setConnectionState(CONNECTION_STATES.CONNECTING);

    // Create socket instance
    const newSocket = io(`${SOCKET_URL}${SOCKET_CONFIG.namespace}`, {
      path: SOCKET_CONFIG.path,
      auth: { token },
      transports: SOCKET_CONFIG.transports,
      reconnectionAttempts: SOCKET_CONFIG.reconnectionAttempts,
      reconnectionDelay: SOCKET_CONFIG.reconnectionDelay,
      reconnectionDelayMax: SOCKET_CONFIG.reconnectionDelayMax,
      timeout: SOCKET_CONFIG.timeout,
      autoConnect: true,
    });

    // CONNECTION EVENT HANDLERS


    newSocket.on('connect', () => {
      console.log('✅ [Socket] Connected:', newSocket.id);
      setConnectionState(CONNECTION_STATES.CONNECTED);
      reconnectAttemptRef.current = 0;
    });

    newSocket.on('connected', (data) => {
      console.log('[Socket] Server confirmed:', data);
      if (data.global_user_id) {
        setGlobalUserId(data.global_user_id);
        // Join user-level room for notifications (mentions, etc.)
        newSocket.emit('join_user_room', { user_id: data.global_user_id });
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('❌ [Socket] Disconnected:', reason);
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
      
      // Clear typing indicators on disconnect
      setTypingUsers({});
    });

    newSocket.on('connect_error', (error) => {
      console.error('⚠️ [Socket] Connection error:', error.message);
      setConnectionState(CONNECTION_STATES.ERROR);
    });

    newSocket.io.on('reconnect_attempt', (attempt) => {
      console.log(`🔄 [Socket] Reconnection attempt ${attempt}`);
      setConnectionState(CONNECTION_STATES.RECONNECTING);
      reconnectAttemptRef.current = attempt;
    });

    newSocket.io.on('reconnect', (attempt) => {
      console.log(`✅ [Socket] Reconnected after ${attempt} attempts`);
      setConnectionState(CONNECTION_STATES.CONNECTED);
      reconnectAttemptRef.current = 0;
      
      // Rejoin previously joined rooms using ref (always current)
      joinedRoomsRef.current.forEach((conversationId) => {
        newSocket.emit('join_convo', { conversation_id: conversationId });
      });
    });

    newSocket.io.on('reconnect_failed', () => {
      console.error('❌ [Socket] Reconnection failed after max attempts');
      setConnectionState(CONNECTION_STATES.ERROR);
    });


    // MESSAGE & FEATURE EVENT HANDLERS (using refs for always-current handlers)
    newSocket.on('new_message', (data) => handlersRef.current.handleNewMessage?.(data));
    newSocket.on('message_confirmed', (data) => handlersRef.current.handleMessageConfirmed?.(data));
    newSocket.on('message_failed', (data) => handlersRef.current.handleMessageFailed?.(data));
    newSocket.on('user_typing', (data) => handlersRef.current.handleTypingStatus?.(data));
    newSocket.on('typing_users', (data) => handlersRef.current.handleTypingUsers?.(data));
    newSocket.on('messages_read', (data) => handlersRef.current.handleMessagesRead?.(data));
    newSocket.on('reaction_update', (data) => handlersRef.current.handleReactionUpdate?.(data));
    newSocket.on('message_deleted', (data) => handlersRef.current.handleMessageDeleted?.(data));
    newSocket.on('message_edited', (data) => handlersRef.current.handleMessageEdited?.(data));
    newSocket.on('message_pinned', (data) => handlersRef.current.handleMessagePinned?.(data));
    newSocket.on('message_unpinned', (data) => handlersRef.current.handleMessageUnpinned?.(data));
    newSocket.on('messages_fetched', (data) => handlersRef.current.handleMessagesFetched?.(data));

    // Presence events
    newSocket.on('presence_update', (data) => handlersRef.current.handlePresenceUpdate?.(data));
    newSocket.on('online_users_status', (data) => handlersRef.current.handleOnlineUsersStatus?.(data));
    newSocket.on('room_presence', (data) => handlersRef.current.handleRoomPresence?.(data));

    // Conversation events
    newSocket.on('added_to_conversation', (data) => handlersRef.current.handleAddedToConversation?.(data));
    newSocket.on('removed_from_conversation', (data) => handlersRef.current.handleRemovedFromConversation?.(data));
    newSocket.on('participant_added', (data) => handlersRef.current.handleParticipantAdded?.(data));
    newSocket.on('participant_removed', (data) => handlersRef.current.handleParticipantRemoved?.(data));
    newSocket.on('host_transferred', (data) => handlersRef.current.handleHostTransferred?.(data));
    newSocket.on('conversation_updated', (data) => handlersRef.current.handleConversationUpdated?.(data));
    newSocket.on('status_updated', (data) => handlersRef.current.handleStatusUpdated?.(data));
    newSocket.on('request_accepted', (data) => handlersRef.current.handleRequestAccepted?.(data));
    newSocket.on('error', (data) => handlersRef.current.handleError?.(data));

    // Notification events (mentions, etc.)
    newSocket.on('notification_created', (data) => handlersRef.current.handleNotificationCreated?.(data));

    // Room management confirmations
    newSocket.on('joined_convo', (data) => {
      setJoinedRooms((prev) => new Set([...prev, data.conversation_id]));
    });

    newSocket.on('left_convo', (data) => {
      setJoinedRooms((prev) => {
        const newSet = new Set(prev);
        newSet.delete(data.conversation_id);
        return newSet;
      });
    });

    // Store refs
    socketRef.current = newSocket;
    setSocket(newSocket);

    // Cleanup on unmount or token change
    return () => {
      // Clear all typing timeouts
      Object.values(typingTimeoutsRef.current).forEach(clearTimeout);
      typingTimeoutsRef.current = {};
      
      // Disconnect socket
      if (newSocket) {
        newSocket.removeAllListeners();
        newSocket.disconnect();
      }
      socketRef.current = null;
      setSocket(null);
      setConnectionState(CONNECTION_STATES.DISCONNECTED);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getActiveToken]); // Only reconnect when token changes, not when handlers update


  // EXPOSED METHODS

  /**
   * Join a conversation room to receive real-time updates
   */
  const joinConversation = useCallback((conversationId) => {
    if (!socketRef.current || !conversationId) return;
    
    socketRef.current.emit('join_convo', { conversation_id: conversationId });
  }, []);

  /**
   * Leave a conversation room
   */
  const leaveConversation = useCallback((conversationId) => {
    if (!socketRef.current || !conversationId) return;
    
    socketRef.current.emit('leave_convo', { conversation_id: conversationId });
  }, []);

  /**
   * Send a message via WebSocket
   * @returns {Promise} Resolves when message is acknowledged
   */
  const sendMessage = useCallback((payload) => {
    return new Promise((resolve, reject) => {
      if (!socketRef.current?.connected) {
        reject(new Error('Socket not connected'));
        return;
      }

      const { 
        conversation_id, 
        message, 
        attachment_id = null,
        reply_to_message_id = null,
        is_quote = false,
        client_temp_id = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      } = payload;

      socketRef.current.emit('send_message', {
        conversation_id,
        message,
        attachment_id,
        reply_to_message_id,
        is_quote,
        client_temp_id,
      });

      // Resolve immediately - the new_message event will update the UI
      resolve({ client_temp_id });
    });
  }, []);

  /**
   * Send typing status indicator
   */
  const sendTypingStatus = useCallback((conversationId, isTyping) => {
    if (!socketRef.current?.connected || !conversationId) return;

    socketRef.current.emit('typing_status', {
      conversation_id: conversationId,
      is_typing: isTyping,
    });
  }, []);

  /**
   * Mark messages as read. If messageId is provided, marks up to that message.
   * If omitted, marks all messages in the conversation as read.
   */
  const markAsRead = useCallback((conversationId, messageId) => {
    if (!socketRef.current?.connected || !conversationId) return;

    const payload = { conversation_id: conversationId };
    if (messageId) payload.message_id = messageId;

    socketRef.current.emit('mark_read', payload);
  }, []);

  /**
   * Add a reaction to a message
   */
  const addReaction = useCallback((conversationId, messageId, emoji) => {
    if (!socketRef.current?.connected) return;

    socketRef.current.emit('add_reaction', {
      conversation_id: conversationId,
      message_id: messageId,
      emoji,
    });
  }, []);

  /**
   * Remove a specific reaction from a message
   */
  const removeReaction = useCallback((conversationId, messageId, emoji) => {
    if (!socketRef.current?.connected) return;

    socketRef.current.emit('remove_reaction', {
      conversation_id: conversationId,
      message_id: messageId,
      emoji,
    });
  }, []);

  /**
   * Delete a message
   */
  const deleteMessage = useCallback((conversationId, messageId) => {
    if (!socketRef.current?.connected) return;

    socketRef.current.emit('delete_message', {
      conversation_id: conversationId,
      message_id: messageId,
    });
  }, []);

  const editMessage = useCallback((conversationId, messageId, newMessage) => {
    if (!socketRef.current?.connected || !newMessage?.trim()) return;
    socketRef.current.emit('edit_message', {
      conversation_id: conversationId,
      message_id: messageId,
      message: newMessage.trim(),
    });
  }, []);

  const pinMessage = useCallback((conversationId, messageId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('pin_message', { conversation_id: conversationId, message_id: messageId });
  }, []);

  const unpinMessage = useCallback((conversationId, messageId) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('unpin_message', { conversation_id: conversationId, message_id: messageId });
  }, []);

  const fetchMessages = useCallback((conversationId, options = {}) => {
    if (!socketRef.current?.connected || !conversationId) return;
    const { before_id, after_id, limit = 50 } = options;
    socketRef.current.emit('fetch_messages', {
      conversation_id: conversationId,
      before_id,
      after_id,
      limit: Math.min(limit, 100),
    });
  }, []);

  const updateStatus = useCallback((conversationId, status) => {
    if (!socketRef.current?.connected || !conversationId || !status) return;
    socketRef.current.emit('update_status', { conversation_id: conversationId, status });
  }, []);

  const acceptRequest = useCallback((conversationId) => {
    if (!socketRef.current?.connected || !conversationId) return;
    socketRef.current.emit('accept_request', { conversation_id: conversationId });
  }, []);

  const getStatus = useCallback((conversationId) => {
    if (!socketRef.current?.connected || !conversationId) return;
    socketRef.current.emit('get_status', { conversation_id: conversationId });
  }, []);

  const addParticipant = useCallback((conversationId, participant) => {
    if (!socketRef.current?.connected || !conversationId) return;
    const { user_id, creator_id, global_user_id } = participant || {};
    if (!user_id && !creator_id && !global_user_id) return;
    socketRef.current.emit('add_participant', {
      conversation_id: conversationId,
      user_id,
      creator_id,
      global_user_id,
    });
  }, []);

  const removeParticipant = useCallback((conversationId, globalUserId) => {
    if (!socketRef.current?.connected || !conversationId || !globalUserId) return;
    socketRef.current.emit('remove_participant', { 
      conversation_id: conversationId, 
      global_user_id: globalUserId 
    });
  }, []);

  const transferHost = useCallback((conversationId, newHostGlobalUserId) => {
    if (!socketRef.current?.connected || !conversationId || !newHostGlobalUserId) return;
    socketRef.current.emit('transfer_host', { 
      conversation_id: conversationId, 
      new_host_global_user_id: newHostGlobalUserId 
    });
  }, []);

  const updateConversation = useCallback((conversationId, updates) => {
    if (!socketRef.current?.connected || !conversationId) return;
    const { display_name, theme, cover_image } = updates || {};
    if (!display_name && !theme && !cover_image) return;
    socketRef.current.emit('update_conversation', {
      conversation_id: conversationId,
      display_name,
      theme,
      cover_image,
    });
  }, []);

  const getOnlineUsers = useCallback((userIds) => {
    if (!socketRef.current?.connected || !userIds?.length) return;
    socketRef.current.emit('get_online_users', { user_ids: userIds });
  }, []);

  const getTypingUsers = useCallback((conversationId) => {
    return typingUsers[conversationId] || [];
  }, [typingUsers]);

  /**
   * Get per-room online users for a conversation
   */
  const getRoomOnlineUsers = useCallback((conversationId) => {
    return roomPresence[conversationId] || { online_users: [], online_count: 0 };
  }, [roomPresence]);

  /**
   * Check if a user is online
   */
  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  /**
   * Force reconnect the socket
   */
  const reconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.connect();
    }
  }, []);

 
  // CONTEXT VALUE
 

  const contextValue = useMemo(() => ({
    // Core socket state
    socket,
    isConnected: connectionState === CONNECTION_STATES.CONNECTED,
    connectionState,
    globalUserId,
    lastError,
    
    // Room management
    joinConversation,
    leaveConversation,
    joinedRooms: Array.from(joinedRooms),
    
    // Messaging
    sendMessage,
    sendTypingStatus,
    markAsRead,
    fetchMessages,
    
    // Reactions
    addReaction,
    removeReaction,
    
    // Message actions
    deleteMessage,
    editMessage,
    pinMessage,
    unpinMessage,
    pinnedMessages,
    
    // Presence & typing
    onlineUsers: Array.from(onlineUsers),
    typingUsers,
    getTypingUsers,
    isUserOnline,
    getOnlineUsers,
    roomPresence,
    getRoomOnlineUsers,
    
    // Conversation management
    updateStatus,
    acceptRequest,
    getStatus,
    addParticipant,
    removeParticipant,
    transferHost,
    updateConversation,
    
    // Utilities
    reconnect,
    CONNECTION_STATES,
    VALID_REACTIONS,
  }), [
    socket,
    connectionState,
    globalUserId,
    lastError,
    joinConversation,
    leaveConversation,
    joinedRooms,
    sendMessage,
    sendTypingStatus,
    markAsRead,
    fetchMessages,
    addReaction,
    removeReaction,
    deleteMessage,
    editMessage,
    pinMessage,
    unpinMessage,
    pinnedMessages,
    onlineUsers,
    typingUsers,
    getTypingUsers,
    isUserOnline,
    getOnlineUsers,
    roomPresence,
    getRoomOnlineUsers,
    updateStatus,
    acceptRequest,
    getStatus,
    addParticipant,
    removeParticipant,
    transferHost,
    updateConversation,
    reconnect,
  ]);

  return (
    <SocketContext.Provider value={contextValue}>
      {children}
    </SocketContext.Provider>
  );
};

export { CONNECTION_STATES };
export default SocketContext;
